import type { App, HomeyAPI, ItemProps } from 'homey-api';
import semver from 'semver';

import { HomeyAppVersionError, HomeySystemVersionError } from '../../lib/InternalError.js';

import { BaseApiStore, type FetchArgs } from '../../store/BaseApiStore.ts';
import { KeyedAsyncQueue } from '../../store/KeyedAsyncQueue';
import { OptimisticEntityUpdates } from '../../store/OptimisticEntityUpdates';

export interface ScriptProps {
  id: string;
  lastExecuted: string | null;
  name: string;
  version: number;
  code: string;
}

type ScriptSummary = Omit<ScriptProps, 'code'>;
type ScriptUpdate = Partial<Pick<ScriptProps, 'name' | 'code'>>;
type ScriptRunResult =
  | { success: true; returns: unknown }
  | { success: false; returns: { message: string; stack?: string } };

type ScriptState = ScriptSummary & {
  code: string | null;
  // The editor draft and navigation warning must only acknowledge confirmed saves.
  savedCode: string | null;
};
type ScriptsById = Record<string, ScriptState>;

interface State {
  data: null;
  byId: ScriptsById | null;
  app: ItemProps<App> | null;
  appInstance: App | null;
  isLoading: boolean;
  error: unknown;
}

export type ScriptsStoreState = State;

export class ScriptsStore extends BaseApiStore<State, App> {
  #optimisticUpdates = new OptimisticEntityUpdates<ScriptState, ScriptUpdate>({
    applyOperation: ScriptsStore.#applyScriptUpdate,
    reconcileMutationResult: ScriptsStore.#reconcileScriptUpdate,
  });
  #persistenceQueue = new KeyedAsyncQueue<string>();
  #lifecycle = {};

  constructor() {
    super('scripts');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      app: null,
      appInstance: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    const lifecycle = this.#lifecycle;

    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ manager: api.apps });

      if (abortController.signal.aborted || this.#lifecycle !== lifecycle) {
        return;
      }

      const appPromise = api.apps.getApp({ id: 'com.athom.homeyscript', $cache: cache });
      const appInstance = await appPromise;

      if (abortController.signal.aborted || this.#lifecycle !== lifecycle) {
        return;
      }

      const satisfiesHomeyVersion = semver.satisfies(api.version ?? '0.0.0', '>=5.0.0', {
        includePrerelease: true,
      });

      if (satisfiesHomeyVersion === false) {
        throw new HomeySystemVersionError('invalid_homey_version');
      }

      const satisfiesAppVersion = semver.satisfies(appInstance.version ?? '0.0.0', '>=3.3.0');

      if (satisfiesAppVersion === false) {
        throw new HomeyAppVersionError('invalid_app_version');
      }

      const baseRevision = this.#optimisticUpdates.captureBaseRevision();
      const scripts = await appInstance.get<Record<string, ScriptSummary>>({ path: 'script' });

      if (abortController.signal.aborted || this.#lifecycle !== lifecycle) {
        return;
      }

      const state = this.get();
      const byId: ScriptsById = {};

      for (const [id, script] of Object.entries(scripts)) {
        byId[id] = {
          ...script,
          // List responses omit code. Keep fetched bodies; null means details have not loaded.
          code: state.byId?.[id]?.savedCode ?? null,
          savedCode: state.byId?.[id]?.savedCode ?? null,
        };
      }

      this.set({
        ...this.createInitialState(),
        byId: this.#optimisticUpdates.reconcileBaseSnapshot(baseRevision, byId),
        app: appInstance.getProps(),
        appInstance: appInstance,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted || this.#lifecycle !== lifecycle) {
        return;
      }

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy(reason: string) {
    this.disconnectManager().catch(console.error);
    this.#clearScriptUpdates();
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.apps.on('app.create', this.handleCreate);
    api.apps.on('app.update', this.handleUpdate);
    api.apps.on('app.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.apps.off('app.create', this.handleCreate);
    api.apps.off('app.update', this.handleUpdate);
    api.apps.off('app.delete', this.handleDelete);
  }

  getInstanceFromCache(app: { id: string }) {
    return this.getCache()[app.id];
  }

  getCache(): Readonly<Record<string, App>> {
    const api = this.getApi();

    return api.apps.getCachedItems('app');
  }

  handleCreate = (createdApp: App) => {
    this.DEBUG && console.info(`create:app:${createdApp.id}`);
    const appInstance = this.getInstanceFromCache(createdApp);

    if (appInstance == null || appInstance.id !== 'com.athom.homeyscript') {
      return;
    }

    const appProps = appInstance.getProps();

    this.set({
      appInstance: appInstance,
      app: appProps,
    });
  };

  handleUpdate = (updatedApp: App) => {
    this.DEBUG && console.info(`update:app:${updatedApp.id}`);

    if (updatedApp.id !== 'com.athom.homeyscript') {
      return;
    }

    this.addToQueue(updatedApp.id, updatedApp);

    this.batch((updateQueue) => {
      const state = this.get();
      const queuedApp = updateQueue['com.athom.homeyscript'];
      const appInstance = this.getInstanceFromCache(queuedApp);

      if (appInstance == null) {
        this.DEBUG && console.info(`update:app:missing-data`);
        return;
      }

      if (state.appInstance == null) {
        this.DEBUG && console.info(`update:app:unknown`);
        return;
      }

      const appProps = appInstance.getProps();

      this.set({
        appInstance: appInstance,
        app: appProps,
      });
    });
  };

  handleDelete = (deletedApp: Pick<App, 'id'>) => {
    this.DEBUG && console.info(`delete:app:${deletedApp.id}`);

    if (deletedApp.id !== 'com.athom.homeyscript') {
      return;
    }

    this.#clearScriptUpdates();
    this.set({
      appInstance: null,
      app: null,
      byId: null,
    });
  };

  restore() {
    const id = this.get().appInstance?.id;

    if (id == null) {
      return;
    }

    const appInstance = this.getInstanceFromCache({ id });

    if (appInstance == null) {
      return;
    }

    const appProps = appInstance.getProps();

    this.set({
      appInstance: appInstance,
      app: appProps,
    });
  }

  async getScript({ scriptId }: { scriptId: string }) {
    const appInstance = this.#getAppInstance();
    const lifecycle = this.#lifecycle;
    const baseRevision = this.#optimisticUpdates.captureBaseRevision();
    const script = await appInstance.get<ScriptProps>({
      path: `script/${encodeURIComponent(scriptId)}`,
    });

    if (this.#lifecycle === lifecycle) {
      const entity = this.#optimisticUpdates.reconcileBaseEntitySnapshot(baseRevision, scriptId, {
        ...script,
        savedCode: script.code,
      });
      this.#publishScript(scriptId, entity);
    }

    return script;
  }

  async createScript({ script }: { script: { name: string; code: string } }) {
    const appInstance = this.#getAppInstance();
    const lifecycle = this.#lifecycle;
    const newScript = await appInstance.post<ScriptProps>({
      path: 'script',
      body: {
        name: script.name,
        code: script.code,
      },
    });

    if (this.#lifecycle === lifecycle) {
      const entity = this.#optimisticUpdates.reconcileBaseEntity(newScript.id, {
        ...newScript,
        savedCode: newScript.code,
      });
      this.#publishScript(newScript.id, entity);
    }

    return newScript;
  }

  async updateScript({ id, script }: { id: string; script: ScriptUpdate }) {
    const appInstance = this.#getAppInstance();
    const currentScript = this.get().byId?.[id];

    if (currentScript == null) {
      throw new Error(`Script with id ${id} is not available.`);
    }

    const operation = { ...script };
    const { mutationId, entity } = this.#optimisticUpdates.beginMutation(
      id,
      currentScript,
      operation,
    );
    this.#publishScript(id, entity);

    return await this.#persistenceQueue.enqueue(id, async () => {
      this.#optimisticUpdates.startPersistence(id, mutationId);

      try {
        const updatedScript = await appInstance.put<ScriptProps>({
          path: `script/${encodeURIComponent(id)}`,
          body: operation,
        });
        const result = this.#optimisticUpdates.finishMutation(id, mutationId, {
          status: 'success',
          baseEntity: { ...updatedScript, savedCode: updatedScript.code },
        });

        if (result.status === 'applied') {
          this.#publishScript(id, result.entity);
        }

        return updatedScript;
      } catch (error) {
        const result = this.#optimisticUpdates.finishMutation(id, mutationId, {
          status: 'failure',
        });

        if (result.status === 'applied') {
          this.#publishScript(id, result.entity);
        }

        throw error;
      }
    });
  }

  async deleteScript({ id }: { id: string }) {
    const appInstance = this.#getAppInstance();
    const lifecycle = this.#lifecycle;

    await this.#persistenceQueue.enqueue(id, async () => {
      await appInstance.delete({ path: `script/${encodeURIComponent(id)}` });

      if (this.#lifecycle !== lifecycle) {
        return;
      }

      const entity = this.#optimisticUpdates.reconcileBaseEntity(id, undefined);
      this.#publishScript(id, entity);
    });
  }

  async testScript({ id, script }: { id: string; script?: { code: string } }) {
    const appInstance = this.#getAppInstance();

    return await appInstance.post<ScriptRunResult>({
      path: `script/${encodeURIComponent(id)}/run`,
      body: {
        code: script?.code,
      },
    });
  }

  #getAppInstance() {
    const appInstance = this.get().appInstance;

    if (appInstance == null) {
      throw new Error('App instance is not available.');
    }

    return appInstance;
  }

  #publishScript(id: string, script: ScriptState | undefined) {
    const state = this.get();

    if (state.byId == null) {
      return;
    }

    const byId = { ...state.byId };

    if (script == null) {
      delete byId[id];
    } else {
      byId[id] = script;
    }

    this.set({ byId });
  }

  #clearScriptUpdates() {
    this.#optimisticUpdates.clear();
    this.#persistenceQueue.clear();
    this.#lifecycle = {};
  }

  static #applyScriptUpdate(script: ScriptState | undefined, update: ScriptUpdate) {
    if (script == null) {
      return undefined;
    }

    return { ...script, ...update };
  }

  static #reconcileScriptUpdate(
    baseScript: ScriptState | undefined,
    mutationScript: ScriptState | undefined,
    update: ScriptUpdate,
  ): ScriptState | undefined {
    if (baseScript == null || mutationScript == null) {
      return undefined;
    }

    return {
      ...baseScript,
      ...('name' in update ? { name: mutationScript.name } : {}),
      ...('code' in update
        ? { code: mutationScript.code, savedCode: mutationScript.savedCode }
        : {}),
    };
  }
}

export const scriptsStore = new ScriptsStore();
